'use client';

import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import { ToastType } from '@shared/ui-components/toast/constants/constants';
import { useGlobalToastContext } from '@shared/ui-components/toast/context/global-toast-context';
import { type ServerActionResponse } from '@shared/utils/types/server-action';

import { useGlobalCmsContentContext } from '../../components/providers/global-cms-context';
import { NOT_AUTHORIZED_PATH } from '../constants/local-paths';
import { type MutationError } from '../types/mutation-error';

type UseMutationHandlerArguments<T> = {
  mutationFunction: (
    arguments_: T
  ) => Promise<MutationError | null | Response | ServerActionResponse>;
  mutationKey?: string[];
  onFailure?: (result: ServerActionResponse) => void;
  onMutate?: (variables: unknown) => Promise<unknown> | unknown;
  onSuccessHandler?: () => void;
  scrollToTop?: boolean;
  showSuccessToast?: boolean;
  successMessage?: string;
};

/**
 * @deprecated Use `useMutation` with custom hook for each new endpoint instead.
 */
export const useMutationHandler = <T>({
  mutationFunction,
  mutationKey,
  onFailure,
  onMutate,
  onSuccessHandler,
  scrollToTop = true,
  showSuccessToast = true,
  successMessage,
}: UseMutationHandlerArguments<T>) => {
  const { addToast } = useGlobalToastContext();
  const { cmsContent } = useGlobalCmsContentContext();
  const router = useRouter();

  const handleScrollToTop = () => {
    if (scrollToTop) {
      setTimeout(() => {
        window.scrollTo({ behavior: 'smooth', top: 0 });
      }, 0);
    }
  };

  const { isPending: isMutationPending, mutate } = useMutation({
    mutationFn: mutationFunction,
    mutationKey,
    onError: (error) => {
      onFailure?.(error);
    },
    onMutate,
    onSuccess: (result) => {
      if (result === null) return;

      if (result?.status === 200) {
        if (onSuccessHandler) {
          onSuccessHandler();
        }

        if (showSuccessToast) {
          addToast(
            {
              description: successMessage ?? cmsContent.successMessage,
              isCloseButtonVisible: false,
              type: ToastType.SUCCESS,
            },
            { timeout: 3000 }
          );
        }

        handleScrollToTop();
        return;
      }

      if (result?.status === 401) {
        router.push(NOT_AUTHORIZED_PATH);

        handleScrollToTop();
        return;
      }

      if (result?.status !== 200) {
        if (!onFailure && result && 'message' in result) {
          addToast(
            {
              closeButtonLabel: cmsContent.closeButtonLabel,
              description: cmsContent.somethingWentWrong,
              type: ToastType.ERROR,
            },
            { timeout: 0 }
          );
        } else {
          onFailure?.(result);
        }

        handleScrollToTop();
      }
    },
  });

  return { isMutationPending, mutate };
};
